// src/utils/selectAll.ts
//
// Funções puras do "selecionar todos entre páginas" (issue #31 / backend #56).
//
// Regra que orienta este módulo: select-all é SÓ escopo de seleção. Nenhuma
// regra de negócio muda, e o modo enumerado continua byte-idêntico — quando o
// modo é 'enumerado', nada daqui entra no payload.

import type { StatusOperacaoLote } from '@/types/lote/OperacaoLote';

/**
 * 'enumerado' — o usuário marcou linhas; o front envia a lista, como sempre fez.
 * 'todos'     — o usuário pediu "selecionar tudo"; o servidor resolve o conjunto
 *               pelo filtro da listagem e o front envia apenas o que ele DESmarcou.
 */
export type ModoSelecao = 'enumerado' | 'todos';

/**
 * Quantos itens a ação vai atingir, para exibir na faixa de seleção.
 *
 * No modo 'todos' é um TETO, não uma promessa (ADR-039 item 6): o total vem da
 * LISTAGEM, e o conjunto que o servidor processa é a listagem filtrada aos
 * acionáveis — itens sem saldo, de outro conferente, ou fora da alçada de quem
 * chamou saem na resolução ou caem em soft-fail. Comportamento esperado, não bug.
 *
 * Consequência para a UI: não prometer "N itens" com base neste número. A verdade
 * é a contagem que a resposta reporta — no corpo do 200/201 inline, ou em
 * totalProcessado/totalIgnorado no polling. Ver `mensagemResultado`.
 */
export const contarSelecionados = (
  modo: ModoSelecao,
  totalItens: number,
  qtdMarcadaOuExcluida: number,
): number =>
  modo === 'enumerado'
    ? qtdMarcadaOuExcluida
    : Math.max(totalItens - qtdMarcadaOuExcluida, 0);

/**
 * Campos aditivos do modo complemento, prontos para espalhar no corpo da rota.
 *
 * `filtro` é OMITIDO quando não informado — a devolução de itens de pré-ordem é
 * a única superfície sem eixo de filtro (a lista é "os itens removíveis desta
 * pré-ordem", e o pai vem da URL), e mandar um filtro vazio ali só criaria
 * superfície para divergir da listagem.
 *
 * `excludeIds` é omitido quando vazio, pelo mesmo motivo: não poluir o corpo com
 * campo sem significado.
 */
export const fragmentoSelectAll = <F, K>(args: {
  filtro?: F;
  excludeIds?: K[];
}): { selectAll: true; filtro?: F; excludeIds?: K[] } => ({
  selectAll: true,
  ...(args.filtro !== undefined ? { filtro: args.filtro } : {}),
  ...(args.excludeIds && args.excludeIds.length > 0 ? { excludeIds: args.excludeIds } : {}),
});

/**
 * Texto do escopo da seleção, em formato único para os dois modos.
 *
 * Um formato só, sempre "X de Y", para o usuário não ter de interpretar duas
 * frases diferentes conforme como chegou à seleção. Concordância: o particípio
 * acompanha a quantidade selecionada; "item/itens" acompanha o total.
 *
 *   textoSelecao(132, 133) -> "Selecionados 132 de 133 itens."
 *   textoSelecao(1, 133)   -> "Selecionado 1 de 133 itens."
 *   textoSelecao(1, 1)     -> "Selecionado 1 de 1 item."
 */
export const textoSelecao = (selecionados: number, total: number): string => {
  const participio = selecionados === 1 ? 'Selecionado' : 'Selecionados';
  const substantivo = total === 1 ? 'item' : 'itens';
  return `${participio} ${selecionados} de ${total} ${substantivo}.`;
};

// --- Mensagem de resultado ---------------------------------------------------

export interface ResultadoLote {
  /**
   * true quando a rota respondeu 202 e o trabalho seguiu em segundo plano.
   * NÃO decide mais se os contadores aparecem — ver `mensagemResultado`.
   */
  assincrono: boolean;
  status?: StatusOperacaoLote;
  totalProcessado?: number | null;
  totalIgnorado?: number | null;
  erroMensagem?: string | null;
}

export interface MensagemResultado {
  texto: string;
  /** Segunda linha, só existe quando o caminho assíncrono informou itens ignorados. */
  detalhe?: string;
  variante: 'success' | 'warning' | 'error';
}

/** Texto base — idêntico nos dois caminhos, e deliberadamente sem contagem. */
export const TEXTO_PROCESSADO = 'Itens processados.';

/**
 * Decide a mensagem de fim de ação.
 *
 * O texto base é o mesmo nos dois caminhos e nunca afirma totalidade ("todos os
 * N itens"); os contadores entram como uma segunda linha discreta, só quando há
 * item ignorado.
 *
 * CORREÇÃO DE PREMISSA (31/07). A versão anterior só emitia a segunda linha no
 * caminho assíncrono, justificando que o inline "não sabe quantos itens foram
 * ignorados". Isso estava errado: o corpo do 200 não traz um contador PRONTO,
 * mas traz `aprovado` por item — e a leitura do backend confirmou que é
 * exatamente esse booleano que vira `totalIgnorado` no 202
 * (`LoteJobRunner:287`, 1:1 com `aprovado === false`). A contagem sempre foi
 * derivável; quem deriva é o chamador, e aqui ela é tratada igual venha de onde
 * vier. Sem isso, o inline reportava sucesso verde sobre um lote inteiramente
 * recusado.
 *
 * O que continua assimétrico, e por isso não entra na mensagem: os MOTIVOS por
 * item. O inline os tem, o 202 não — o job guarda só contadores. Listar motivos
 * no inline faria a mesma ação ensinar duas expectativas conforme o tamanho do
 * lote, que é o "confiar no silêncio" que este módulo evita. (O modo enumerado
 * segue listando motivos: ele é histórico e intocado — premissa #9.)
 *
 * Validado em produção-dev: um lote resolveu 133 itens e processou 22 (o resto
 * fora da alçada do usuário). Uma mensagem que prometesse 133 teria mentido por 111.
 */
export const mensagemResultado = (r: ResultadoLote): MensagemResultado => {
  if (r.status === 'FALHOU') {
    return {
      texto: 'Não foi possível concluir o processamento.',
      detalhe: r.erroMensagem ?? undefined,
      variante: 'error',
    };
  }

  const ignorados = r.totalIgnorado ?? 0;
  if (ignorados > 0) {
    const processados = r.totalProcessado ?? 0;
    return {
      texto: TEXTO_PROCESSADO,
      detalhe: `${processados} processados, ${ignorados} ignorados.`,
      variante: 'warning',
    };
  }

  return { texto: TEXTO_PROCESSADO, variante: 'success' };
};

/**
 * Contadores do caminho INLINE, derivados do array de decisões por item.
 *
 * Existe para que o inline fale a mesma língua do 202: lá o backend conta o item
 * bem-sucedido como processado e o resto como ignorado (`LoteJobRunner:287`), e
 * aqui a conta é a mesma sobre o mesmo array — o corpo do 200 no selectAll é
 * byte-a-byte o do modo enumerado.
 *
 * O NOME DO CAMPO DE SUCESSO MUDA POR SUPERFÍCIE: a aprovação responde
 * `aprovado`, a reversão responde `revertido`. Daí o seletor. O default cobre a
 * aprovação para que a superfície 1, já em produção, não precise ser tocada —
 * a garantia é que os testes dela passam sem alteração.
 */
export const contadoresInline = <T,>(
  decisoes: readonly T[],
  foiProcessado: (decisao: T) => boolean = (d) =>
    (d as { aprovado?: boolean | null }).aprovado === true,
): { totalProcessado: number; totalIgnorado: number } => {
  const totalProcessado = decisoes.filter(foiProcessado).length;
  return { totalProcessado, totalIgnorado: decisoes.length - totalProcessado };
};
